import { sortBy } from 'lodash';
import {
  ExchangeType,
  PRIOR_YEAR,
  PRODUCT_TYPE_ENUM_MAP,
  SELECTED_UNIT_ENUM_OBJECT_MAP,
  UNIT,
} from '../../settings/settings';
import { isAPP } from 'src/utils/advice/advice-utility';

/**
 * @typedef {object} AarEnumValue
 * @property {number} enumerationID
 * @property {string} enumerationName
 * @property {string} [code]
 */

/**
 * @typedef {object} AarInvestmentVehicleKey
 * @property {string} @Type Always 'InvestmentVehicleKey'
 * @property {string} ticker
 * @property {string} trpcode // LOWER case
 */

/**
 * @typedef {object} AarMutualFundAgreementKey
 * @property {string} @Type Always 'MutualFundAgreementKey'
 * @property {string} agreementID
 * @property {string} agreementNumber The core part of the agreement number, EXCLUDING the checksum
 * @property {number} checkSum
 * @property {AarEnumValue} productType
 * @property {string} [trpcode] // Sometimes included and sometimes not--included in search examples but not in validation examples
 */

/**
 * @typedef {object} AarPositionKey
 * @property {string} @Type Always 'PositionKey'
 * @property {AarInvestmentVehicleKey} investmentVehicleKey
 * @property {AarMutualFundAgreementKey} agreementKey
 */

/**
 * @typedef {object} AarTransactionRecord
 * @property {string} @Type Always 'TransactionRecord'
 * @property {AarEnumValue} subType
 * @property {AarEnumValue} unit
 * @property {number} amount
 * @property {number} [iraWithholding] Default in UI is 10.
 * @property {AarPositionKey} sourcePositionKey
 * @property {AarPositionKey} targetPositionKey
 */

/**
 * @typedef {object} AarTransaction
 * @property {string} @Type Always 'Transaction'
 * @property {string} transactionID Accepts 'generated' for new transactions
 * @property {AarTransactionRecord[]} transactionRecords
 */

export * from './createApiPostActionCreator';

/**
 * Given a selected unit, amount, and fund will translate 100% or all share quantity to ALL_SHARES, otherwise
 * returns the original.
 *
 * @param {ExchangeFund} fund
 * @param {string} unit
 * @param {number} amount
 * @returns {{effectiveUnit: string, effectiveAmount: number}}
 */

export function getEffectiveUnitAndAmount(fund, unit, amount) {
  let effectiveAmount = amount;
  let effectiveUnit = unit;
  switch (unit) {
    case UNIT.PERCENT:
      if (amount === 100) {
        effectiveAmount = 0;
        effectiveUnit = UNIT.ALL_SHARES;
      }
      break;

    case UNIT.SHARES:
      if (amount === fund.shares) {
        effectiveAmount = 0;
        effectiveUnit = UNIT.ALL_SHARES;
      }
      break;
    case UNIT.ALL_SHARES:
      effectiveAmount = 0;
      break;
    default:
      break;
  }

  return { effectiveAmount, effectiveUnit };
}

/**
 * Given a fund, return an AAR MutualFundAgreementKey
 * @param {UserFund} fund
 * @returns {typeof AarMutualFundAgreementKey}
 */
export function fundToMutualFundAgreementKey(toFund, newFundCode = 0) {
  const {
    agreementID: toFundAgreementID,
    agreementNumber: toFundAgreementNumber = '',
    investmentVehicleKey: { trpcode: investmentVehicleKeyTrpCode } = {},
    productType: toFundProductType = 'MutualFund',
    newFund: toFundIsNewFund,
    trpCode,
  } = toFund;

  const toFundTrpCode = trpCode || investmentVehicleKeyTrpCode || '';

  // when we receive agreement number we get it with checksum together like
  //    5001400379-7
  // but search services are passthrough that need it split to two fields
  /** @type {string[]} */
  const agreementNumberParts = toFundAgreementNumber.split('-');
  return {
    '@Type': isAPP(toFundProductType)
      ? 'AgreementKey'
      : 'MutualFundAgreementKey', //DOIT AgreementKey for APP exchange
    TRPCode: toFundTrpCode,
    // Existing fund
    checkSum:
      agreementNumberParts.length > 1 ? parseInt(agreementNumberParts[1]) : 0,
    ...{
      agreementID: toFundAgreementID,
      productType: {
        enumerationID: PRODUCT_TYPE_ENUM_MAP[toFundProductType],
        enumerationName: toFundProductType,
      },
    },
    ...(!toFundIsNewFund && {
      agreementNumber:
        agreementNumberParts.length > 0 ? agreementNumberParts[0] : '',
    }),
    ...(toFundIsNewFund && {
      agreementID: `newFund-${toFundTrpCode}-${newFundCode}`,
    }),
  };
}

/**
 * Given a fund, return an AAR PositionKey
 * @param {UserFund} fund
 * @returns {typeof AarPositionKey}
 */
export function fundToPositionKey(fund, newFundCode = 0) {
  const { investmentVehicleKey: { trpcode } = {}, ticker, trpCode } = fund;
  const selectedTrpCode = trpCode || trpcode || '';
  // Some funds have ticker set to fundName, remove this for submission payload
  const sanitizedTicker = ticker && ticker.length === 5 ? ticker : '';

  return {
    '@Type': 'PositionKey',
    investmentVehicleKey: {
      '@Type': 'InvestmentVehicleKey',
      ticker: sanitizedTicker,
      trpcode: selectedTrpCode,
      cusip: fund.cusip,
    },
    agreementKey: fundToMutualFundAgreementKey(fund, newFundCode),
  };
}

/**
 * Given a single source and single target fund, creates a transaction record in the format the server needs.
 * @param {ExchangeFund} fromFund
 * @param {ExchangeFund} toFund
 * @param {string} unit From settings UNIT.DOLLARS, UNIT.PERCENT, or UNIT.SHARES
 * @param {number} amount
 * @returns {typeof AarTransactionRecord}
 */
function createExchangeOneTransactionRecord(
  fromFund,
  toFund,
  unit,
  amount,
  newFundCode = 0
) {
  const { newFund = false } = toFund;
  const { effectiveAmount, effectiveUnit } = getEffectiveUnitAndAmount(
    fromFund,
    unit,
    amount
  );
  return {
    '@Type': 'TransactionRecord',
    subType: newFund
      ? {
          enumerationID: 1032,
          enumerationName: 'NewAccount',
        }
      : {
          enumerationID: 1033,
          enumerationName: 'ExistingAccount',
        },
    unit: SELECTED_UNIT_ENUM_OBJECT_MAP[effectiveUnit],
    amount: effectiveAmount,
    sourcePositionKey: fundToPositionKey(fromFund),
    targetPositionKey: fundToPositionKey(toFund, newFundCode), // fromFund added for payloads for new funds
  };
}

/**
 *
 * @type {Object.<string, function(ExchangeFund[], ExchangeFund[]): AarTransactionRecord[]>}}
 */
const exchangeTransactionRecordsCreators = {
  /**
   * Creates an array with just one record for a one-to-one transaction.
   *
   * @param {ExchangeFund[]} exchangeFromSummaryFundList
   * @param {ExchangeFund[]} exchangeToSummaryFundList
   * @param {BankAgreementKey} bankAgreementKey
   * @returns {AarTransactionRecord[]}
   */
  [ExchangeType.ONE_TO_ONE](
    exchangeFromSummaryFundList,
    exchangeToSummaryFundList
  ) {
    const from = exchangeFromSummaryFundList[0];
    const newFundCode = Date.now();
    return [
      createExchangeOneTransactionRecord(
        from,
        exchangeToSummaryFundList[0],
        from.grossUnit,
        from.grossAmount,
        newFundCode
      ),
    ];
  },

  /**
   * Creates an array with just one record for a one-to-many transaction.
   *
   * @param {ExchangeFund[]} exchangeFromSummaryFundList
   * @param {ExchangeFund[]} exchangeToSummaryFundList
   * @param {BankAgreementKey} bankAgreementKey
   * @returns {AarTransactionRecord[]}
   */
  [ExchangeType.ONE_TO_MANY](
    exchangeFromSummaryFundList,
    exchangeToSummaryFundList
  ) {
    const newFundCode = Date.now();
    let allocatedAmount = 0;
    const from = exchangeFromSummaryFundList[0];
    let effectiveUnit = from.grossUnit;
    let effectiveFromAmount = from.grossAmount;
    if (effectiveUnit === UNIT.ALL_SHARES) {
      effectiveUnit = UNIT.SHARES;
      effectiveFromAmount = from.availableShares;
    }

    // if there is an "all remaining" we need to ensure it is last in the list
    const sortedToList = sortBy(
      exchangeToSummaryFundList,
      (fund) => fund.grossUnit === UNIT.ALL_REMAINING
    );

    return sortedToList.map((to) => {
      let amount;
      switch (to.grossUnit) {
        case UNIT.DOLLARS:
          amount = to.grossAmount;
          break;

        case UNIT.PERCENT:
          amount = effectiveFromAmount * (to.grossAmount / 100);
          if (
            allocatedAmount + amount >= effectiveFromAmount &&
            from.grossUnit === UNIT.ALL_SHARES
          ) {
            amount = 0;
            effectiveUnit = UNIT.ALL_SHARES;
          }
          break;

        case UNIT.ALL_REMAINING:
          if (from.grossUnit === UNIT.ALL_SHARES) {
            amount = 0;
            effectiveUnit = UNIT.ALL_SHARES;
          } else {
            amount = effectiveFromAmount - allocatedAmount;
          }
          break;
        default:
          break;
      }
      allocatedAmount += amount;
      return createExchangeOneTransactionRecord(
        from,
        to,
        effectiveUnit,
        amount,
        newFundCode
      );
    });
  },

  /**
   * Creates an array with just one record for a many-to-one transaction.
   *
   * @param {ExchangeFund[]} exchangeFromSummaryFundList
   * @param {ExchangeFund[]} exchangeToSummaryFundList
   * @returns {AarTransactionRecord[]}
   */
  [ExchangeType.MANY_TO_ONE](
    exchangeFromSummaryFundList,
    exchangeToSummaryFundList
  ) {
    const newFundCode = Date.now();
    const to = exchangeToSummaryFundList[0];
    return exchangeFromSummaryFundList.map((from) =>
      createExchangeOneTransactionRecord(
        from,
        to,
        from.grossUnit,
        from.grossAmount,
        newFundCode
      )
    );
  },
};

/**
 * Given exchange transaction information, creates an AAR Transaction.
 *
 * @param {string|null} exchangeContributionYear CURRENT_YEAR or PRIOR_YEAR or null
 * @param {number} exchangeFederalWithholdingPercent
 * @param {ExchangeFund[]} exchangeFromSummaryFundList
 * @param {ExchangeFund[]} exchangeToSummaryFundList
 * @param {string} exchangeType
 * @param {bankAgreementKey} bankAgreementKey
 * @returns {typeof AarTransaction}
 */
export function createExchangeTransaction(
  exchangeContributionYear,
  exchangeFederalWithholdingPercent,
  exchangeFromSummaryFundList,
  exchangeToSummaryFundList,
  exchangeType,
  bankAgreementKey
) {
  /**
   * @type {typeof AarTransaction}
   */

  if (exchangeToSummaryFundList) {
    if (
      exchangeToSummaryFundList.newFund &&
      exchangeToSummaryFundList.ticker === 'TRLAX'
    ) {
      bankAgreementKey = null;
    }
  }

  const transaction = {
    '@Type': 'Transaction',
    transactionID: 'generated',
    contributionYear:
      exchangeContributionYear === PRIOR_YEAR
        ? {
            enumerationID: 101,
            enumerationName: 'PriorYear',
          }
        : {
            enumerationID: 100,
            enumerationName: 'CurrentYear',
          },
    type: {
      enumerationID: 132,
      enumerationName: 'Exchange',
    },
    bankAgreementKey: bankAgreementKey,
    transactionRecords: exchangeTransactionRecordsCreators[exchangeType](
      exchangeFromSummaryFundList,
      exchangeToSummaryFundList
    ),
  };

  if (exchangeFederalWithholdingPercent > 0) {
    transaction.transactionRecords.forEach(
      (rec) => (rec.iraWithholding = exchangeFederalWithholdingPercent)
    );
  }

  return [transaction];
}
